import actionTypes, * as actionGroups from "app/static/frontend/sheets/sheet-action-types"
import sheetConstants from "app/static/frontend/sheets/constants"
import sortTypes from "app/static/frontend/sheets/sort-types"
import { patchSavedSearch } from "app/static/frontend/search-new/action-creators"
import { extractIdFromSheetKey } from "app/static/frontend/sheets/selectors/sheet-selectors"
import * as djangioActionCreators from "shared/djangio/action-creators"
import BACKENDERROR from "app/static/frontend/imports/backenderror"
import { objectFrom } from "shared/imports/sharedHelperFunctions"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"
import * as outboxActionCreators from "app/static/frontend/outbox/action-creators"
import { pushPage } from "global-action-creators"
import swalConfigs from "swalConfigs"
import swal from "sweetalert"
import { getSafedSearchRowId } from "app/static/frontend/sheets/mantine-sheet/mantine-sheet-helper-functions"

// export { patchSavedSearch }
const { Sheet, Column } = DjangIO.app.userdata.spreadsheet.models

export const initSheet = (id, kwargs = {}) => ({ type: actionTypes.SHEET_INIT, id, kwargs })

/**
 * Load a particular sheet
 * @param {String} id - The sheet key of the form "sheet:1239"
 * @param {Integer} sheetId - The database ID of the sheet
 * @param {Boolean} useColumnCache - Whether to request cached column values from the server for each column, if available.
 */
export const loadSheet = (id, sheetId, useColumnCache = false) =>
    djangioActionCreators.resourceGetDetail(
        Sheet.objects.filter({ fetch_cache: useColumnCache }),
        actionGroups.getSheet,
        sheetId,
        { id, useColumnCache },
    )

/**
 * Load a public sheet and request the column cache
 *
 * This function is distinct from loadSheet(), because:
 * - It forces fetch_cache: true, which is ignored by the server, but helpful for context
 * - It does NOT pass useColumnCache to the reducer, which allows the cached response to set isLoading: false
 *
 * @param {String} id - The sheet key of the form "sheet:1239"
 * @param {Integer} sheetId - The database ID of the sheet
 */
export const loadPublicSheet = (id, sheetId) =>
    djangioActionCreators.resourceGetDetail(
        Sheet.objects.filter({ fetch_cache: true }),
        actionGroups.getSheet,
        sheetId,
        { id },
    )

export const loadSheetForSheetForm = (id, sheetId) =>
    djangioActionCreators.resourceGetDetail(Sheet.objects.action("_noColumns"), actionGroups.getSheet, sheetId, { id })

export const initSheetForm = () => ({ type: actionTypes.INIT_SHEET_FORM })

export const loadBigSheet = (id, sheetId, searchingValue, sortingId, sortType) =>
    djangioActionCreators.resourceGetDetail(
        Sheet.objects.action("big").filter({
            searchingValue: JSON.stringify(searchingValue),
            sortingId,
            sortType,
        }),
        actionGroups.getBigSheet,
        sheetId,
        { id },
    )

export const loadBigSheetInterval = (
    id,
    sheetId,
    searchingValue,
    sortingId,
    sortType,
    offset,
    limit = sheetConstants.TABLE_SCROLL_INTERVAL,
) =>
    djangioActionCreators.resourceGetDetail(
        Sheet.objects
            .action("big")
            .filter({
                searchingValue: JSON.stringify(searchingValue),
                sortingId,
                sortType,
            })
            .offset(offset)
            .limit(limit),
        actionGroups.getBigSheetInterval,
        sheetId,
        { id },
    )

// this function just passes the response through before passing the object to update the resource table
// with the result from the server. The
const defaultResponseTransform = (response) => response

/**
 * This function updates a row in a resource table
 * @name updateResourceTableRow
 * @function
 * @param {Function} cellStateMappingFunc -  a function to map a resource to a cell state with signature (resource => string ['default' if no other style])
 * @param {Integer} datumId - the database id of the object that represents the row we are trying to patch. Used
 * @param {responseTransform} - function to parse updateArgs, which may need to be changed depending
 *  on where they are coming from
 * @param {tableId} - the name of the ResourceTable that corresponds to an internal sheet key
 * @param {updateArgs} - an object of key/value pairs to change for the row
 * @returns {Object} - Redux Action
 */
export const updateResourceTableRow = ({
    cellStateMappingFunc,
    datumId,
    responseTransform = defaultResponseTransform,
    tableId,
    updateArgs,
} = {}) => ({
    type: actionTypes.RESOURCE_TABLE_UPDATE_ROW,
    sheetKey: tableId,
    datumId,
    updateArgs: responseTransform(updateArgs), // provide a way to clean the response for the action
    cellStateMappingFunc,
})

export const loadResourceTable = (
    id,
    resource,
    filter,
    sortKey,
    scrollInterval = sheetConstants.TABLE_SCROLL_INTERVAL,
    cellStateMappingFunc = undefined,
    canOrderRows = false,
) => {
    const filters = { ...filter }
    if (canOrderRows) {
        filters.count = true
    }

    return djangioActionCreators.resourceGetList(
        djangioActionCreators.convertResourceToQueryset(resource).limit(scrollInterval).order_by(sortKey),
        actionGroups.getResourceTable,
        filters,
        {
            id,
            cellStateMappingFunc,
            resourceTableRowFiltersForRequest: filters,
            url: djangioActionCreators
                .convertResourceToQueryset(resource)
                .limit(scrollInterval)
                .order_by(sortKey)
                .filter(filters)
                .buildUrl(),
        },
    )
}

export const createEmailFromSheet =
    ({ id, name, advSearchParams, ...rest }) =>
    async (dispatch) => {
        swal({
            title: "Generating Email",
            text: "Just a moment...",
            icon: "info",
            buttons: {},
            closeOnClickOutside: false,
        })
        dispatch(pushPage())
        const bulkEmail = await dispatch(outboxActionCreators.createBulkEmail())
        await dispatch(
            outboxActionCreators.addPeopleFromSheet({
                sheetId: id,
                advSearchParams,
                sheetName: name,
                ...rest,
            }),
        )
        swal.close()
        dispatch(outboxActionCreators.segueToOutbox({ bulkEmailId: bulkEmail.id }))
    }

/**
 * Store the ResourceTableContainer's props so that other components down the tree can access them
 * @param {Object} props - the ResourceTableContainer's props
 * @returns {Object} - Redux action with props of interest to be stored
 */
export const storeResourceTableConfiguration = (props) => {
    const {
        columns,
        datumHeaderCellTitle,
        datumUrlOverride,
        dataType,
        resourceTableDatumTooltipText,
        resourceTableTooltipText,
        rowFilter,
        rowResource,
        scrollInterval,
        swalConfig,
        cellStateStyleMap,
        datumColumnSortingQuerymethod,
    } = props
    return {
        type: actionTypes.STORE_RESOURCE_TABLE_CONFIGURATION,
        id: props.id, // pass id for the table
        resourceTableConfiguration: {
            columns,
            datumHeaderCellTitle,
            datumUrlOverride,
            dataType,
            resourceTableDatumTooltipText,
            resourceTableTooltipText,
            rowFilter,
            rowResource,
            scrollInterval,
            swalConfig,
            cellStateStyleMap,
            datumColumnSortingQuerymethod,
        },
    }
}

export const loadResourceTableInterval = (id, resource, offset, limit, filter = {}, sortKey, cellStateMappingFunc) =>
    djangioActionCreators.resourceGetList(
        djangioActionCreators.convertResourceToQueryset(resource).limit(limit).offset(offset).order_by(sortKey),
        actionGroups.getResourceTableInterval,
        filter,
        {
            id,
            cellStateMappingFunc,
            resourceTableRowFiltersForRequest: filter,
        },
    )

// loads all of the columns for a spreadsheet
export const loadColumns = (id, sheet) =>
    djangioActionCreators.resourceGetList(Column, actionGroups.getColumns, { sheet }, { id })

/**
 * send a request to the api to patch a column
 * @param {String} id - the sheet key, of the form "sheet:3904"
 * @param {Integer} columnId - the database id of the column
 * @param {Object} kwargs (optional) - optional object of arguments to pass to patch the column
 * @param {Boolean} skipDataUpdate - if this patch request is to the column itself, not its data
 *         useful because the return values of the patch Column and patch column data are different
 * For example, if you'd like to patch the name, then kwargs should be {name: "new column name"}
 * @returns {Function} - A thunk
 */
export const patchColumn = (id, columnId, kwargs, skipDataUpdate = false) =>
    djangioActionCreators.resourcePatchDetail(
        Column,
        skipDataUpdate ? actionGroups.patchColumn : actionGroups.patchColumnData,
        columnId,
        kwargs,
        { id, columnId },
    )

// send a request to the backend to save the current column order in the store
export const saveColumnOrder = (sheetId, payload) =>
    djangioActionCreators.resourcePatchAction(
        Sheet,
        actionGroups.saveColumnOrder,
        sheetId,
        { actionName: "save_column_order" },
        { payload: payload.toJS ? payload.toJS() : payload }, // pass in the payload, not sure if we can move the toJS()
    )

// send a request to the backend to duplicate the sheet
export const duplicateSheet = (
    sheetId,
    sheet_name, // eslint-disable-line camelcase
) =>
    djangioActionCreators.resourcePatchAction(
        Sheet,
        actionGroups.duplicateSheet,
        sheetId,
        { actionName: "duplicate_sheet" },
        { payload: sheet_name }, // pass in the payload
    )

export const postSheet = (kwargs) => djangioActionCreators.resourcePostList(Sheet, actionGroups.postSheet, kwargs)

/**
 * Action creator for patching a Sheet
 * Uses djangio action creators and dispatches START/SUCCESS actions
 * @param {String} id - the sheet key, of the form "sheet:3904". Used to patch a particular object of the resource
 * as well as update the redux store with loading state
 * @param {Integer} kwargs - additional arguments to pass to the backend
 * @returns {Function} - returns a function that returns a Promise
 */
export const patchSheet = (id, kwargs) =>
    djangioActionCreators.resourcePatchDetail(Sheet, actionGroups.patchSheet, extractIdFromSheetKey(id), kwargs, {
        id,
        ...kwargs,
    })

/**
 * Action creator for toggling the Math Footer
 * Uses djangio action creators and dispatches START/SUCCESS actions
 * differs from patch sheet since it updates is a loading state specific to the math footer db request
 * @param {String} id - the sheet key, of the form "sheet:3904". Used to patch a particular object of the resource
 * as well as update the redux store with loading state
 * @param {Integer} kwargs - additional arguments to pass to the backend
 * @returns {Function} - returns a function that returns a Promise
 */
export const patchSheetMathFooter = (id, kwargs) => {
    return djangioActionCreators.resourcePatchDetail(
        Sheet,
        actionGroups.patchShouldShowSheetMathBar,
        extractIdFromSheetKey(id),
        kwargs,
        { id, ...kwargs },
    )
}

// resize a column
export const resizeColumn = (id, width, columnId) => ({
    type: actionTypes.RESIZE_COLUMN,
    columnId,
    width,
    id,
})

// make a network request to patch the column size
export const patchColumnSize = (id, width, columnId) => (dispatch) => {
    columnId === sheetConstants.DATUM_COLUMN_INDEX // eslint-disable-line no-unused-expressions
        ? dispatch(patchSheet(id, { datum_column_width: width }))
        : dispatch(patchColumn(id, columnId, { width }, true))
}

// reorder columns in the store
export const reorderColumns = (id, payload) => ({ type: actionTypes.REORDER_COLUMN, id, ...payload })

export const renameColumn = (id, columnId, name) => (dispatch) => {
    dispatch({
        type: actionTypes.RENAME_COLUMN,
        name,
        columnId,
        id,
    })
    dispatch(patchColumn(id, columnId, { name }, true))
}

/**
 * Action creator for toggling whether a column should be hidden in public sheets
 * @param {String} id - the sheet key, of the form "sheet:3904"
 * @param {Integer} columnId - the database id of the column
 * @returns {Function} - returns a function that returns a Promise
 */
export const toggleColumnIsHiddenOnPublic = (id, columnId, isHidden) => (dispatch) => {
    dispatch({
        type: actionTypes.TOGGLE_COLUMN_IS_HIDDEN_ON_PUBLIC,
        isHidden,
        columnId,
        id,
    })
    dispatch(patchColumn(id, columnId, { should_hide_column_on_public: isHidden }))
}

/**
 * Action creator for toggling whether a column should be sorted by default in public sheets
 * @param {String} id - the sheet key, of the form "sheet:3904"
 * @param {Integer} columnId - the database id of the column
 * @param {Integer} defaultSortType - the value of the sort type of the column
 */
export const toggleDefaultSortOnColumn = (id, columnId, defaultSortType) => (dispatch) => {
    dispatch({
        type: actionTypes.TOGGLE_COLUMN_DEFAULT_SORTING,
        defaultSortType,
        columnId,
        id,
    })
    dispatch(patchColumn(id, columnId, { default_sort_type: defaultSortType }))
}

export const toggleDatumColumnIsHiddenOnPublic = (id, isHidden) => (dispatch) => {
    const showDatumColumn = !isHidden
    dispatch({ type: actionTypes.TOGGLE_DATUM_COLUMN_IS_HIDDEN_ON_PUBLIC, showDatumColumn, id })
    dispatch(patchSheet(id, { show_datum_column: showDatumColumn }))
}

// simple action creators
export const editColumnData = (id, columnId, rowId, newValue) => ({
    type: actionTypes.EDIT_COLUMN,
    columnId,
    rowId,
    newValue,
    id,
})

export const createBlankSheet = ({ ...args }) => ({ type: actionTypes.CREATE_BLANK_SHEET, ...args })

export const editSheet = (id, payload) => ({ type: actionTypes.EDIT_SHEET, id, payload })

// action creator that toggles settings about the public sheet
export const togglePublicSheet = (id, payload) => ({ type: actionTypes.TOGGLE_PUBLIC_SHEET, id, payload })

// action creator that toggles settings about the public sheet
export const togglePublicSheetPasswordRequired = (id, payload) => ({
    type: actionTypes.TOGGLE_PUBLIC_SHEET_PASSWORD_REQUIRED,
    id,
    payload,
})

// a hack to unclick a cell
export const unclickCell = (id) => ({ type: actionTypes.CLICK_CELL, id })

export const setErrorCell = (id, rowId, columnId) => ({
    type: actionTypes.UPDATE_CELL_STATE,
    id,
    rowId,
    columnId,
    state: "error",
})

export const updateCache = (row, columnId, newValue) => {
    if (row && row._valuesCache) {
        row._valuesCache[columnId] = newValue
    }
}

const extractIds = (arr) => arr?.map((item) => (item && typeof item === "object" ? item?.id : item))

/**
 * patch the column data, if it is different than the value for the corresponding row column
 * in the store
 * @param {Object}   options
 * @param {String}   options.id - the sheet key in the store
 * @param {Integer}  options.columnId - the column id of the cell - example 0,1,2 if there are 3 columns
 * @param {Integer}  options.rowId - the datum id of the cell to update
 * @param {String}   options.newValue - the new value the user entered in the cell. May be unchanged from the old value
 * @param {String}   options.toStoreValue - the new value of the column in the format the store expects
 * @param {Array}    options.includeRowIds - If true a list of visible row ids will be sent to the client
 */
export const patchColumnData =
    ({ id, columnId, rowId, newValue, toStoreValue, includeRowIds = false, row = null }) =>
    async (dispatch, getState) => {
        // clear the clicked row and column ids so that the UI doesn't show a select
        // the logic to show a cell vs a select in a cell is determined by rowId and columnId,
        // since a cell well have editing=True when the rowId and columnId in the store match
        // its row and column ids
        dispatch(unclickCell(id)) // eslint-disable-line no-dupe-keys

        // Get sheet and column from the store
        const sheet = getState().sheet.get(String(id))
        const column = sheet.get("columns").find((col) => col.get("id") === columnId)
        if (!rowId) {
            rowId = getSafedSearchRowId(row)
        }
        const oldRowValue = String(column.getIn(["data", String(rowId)]))

        // Return without updating if the value didn't change
        if (oldRowValue === newValue) {
            return
        }

        // Preemptively alter the column before sending the patch request
        dispatch(editColumnData(id, columnId, rowId, toStoreValue || newValue))

        // Create the updated column object
        // We use null instead of undefined for cleared values because:
        // 1. undefined is not a valid JSON value and would be stripped when sending data to the server
        // 2. passing undefined was not properly clearing the cell, causing the old value to be retained
        // Using null ensures the value is explicitly cleared and persisted correctly
        let updatedColumn = column.set("data", {
            [rowId]: Array.isArray(newValue) ? extractIds(newValue) : newValue ?? null,
        })

        if (includeRowIds) {
            updatedColumn = updatedColumn.set(
                "row_ids",
                sheet.get("row_data").map((row) => row.get("id")),
            )
        }
        //Updates the value in Cache for that row
        if (row) {
            updateCache(row, columnId, newValue)
        }

        // Send the request to patch the column
        const patchColumnRequest = dispatch(patchColumn(id, columnId, updatedColumn.toJS()))

        // If the request fails let the user know something went wrong
        try {
            await patchColumnRequest
        } catch (e) {
            console.error(e)
            if (row) {
                updateCache(row, columnId, oldRowValue)
            }
            return dispatch(setErrorCell(id, rowId, columnId))
        }

        // Otherwise update cell value once the request completes
        return dispatch({
            type: actionTypes.RESET_CELL_STATE,
            id,
            rowId,
            columnId,
        })
    }

/**
 * Edit some attribute(s) (the fields in updateArgs) of a row in a resource table
 * Optimistically update the data in the row of the table
 * Automatically make a PATCH request to the table 's resource with updateArgs as the request payload
 * Patches will only succeed if the allowed_patch_fields list on the resource contains the field(s) being patched
 * An optional action creator can be provided to handle custom patch logic. This action creator
 * takes in 1 object as an argument and is passed all of the args passed from the caller function. If an action creator
 * is provided, a network request will NOT be made. Be sure to call the network request in your action creator
 * if your intention is for a network request to be made
 * @param {String} tableId - The string table id in the store - used to correctly find the data to update
 * @param {Integer} datumId - the database id of the object that represents the row we are trying to patch. Used
 * to make the network request
 * @param {Object} updateArgs - An object with fields to update
 * @param {DjangIO.Model} resource - a DjangIO resource, representing one of our Django models
 * @param {Function} optionalCellStateMappingFunc - a function to map a resource to a cell state with signature (resource => string ['default' if no other style])
 * @param {Function} optionalActionCreator - an action creator with the signature ({args}) => Any
 * @param {Function} optionalalResponseTransform - A function to preprocess the response from the server and format it for RESOURCE_TABLE_UPDATE_ROW
 *
 * @returns {Function} - Redux Thunk
 *
 * Additional Notes:
 * It is possible to update fields that are not shown in the table. The patch will succeed if it is possible to patch the
 * field(s) via the API
 *
 * Please use the exposed function defined below that takes 1 object as an argument
 */
export const editResourceTableRow =
    (
        tableId,
        datumId,
        updateArgs,
        optionalCellStateMappingFunc,
        optionalActionCreator,
        optionalResponseTransform = defaultResponseTransform,
    ) =>
    async (dispatch, getState) => {
        // unclick the cell so that the cell isn't focused
        // or has a select that is "sort of" open
        dispatch(unclickCell(tableId))

        if (!datumId) {
            throw Error("You must provide a datumId to editResourceTableRow")
        }

        // warn the user if they are passing more than one key in updateArgs, the field that the column corresponds to must
        // be the first key
        if (!Object.keys(updateArgs).length) {
            throw Error("You must provide updateArgs to edit a resource")
        } else if (Object.keys(updateArgs).length > 1) {
            console.warn(
                `Passing more then 1 key in updateArgs. This is allowed, but make sure the field key that corresponds to the column is the first key`,
            )
        }

        // grab the resource from the configuration object
        const resource = getState().sheet.getIn([tableId, "resourceTableConfiguration", "rowResource"])
        // update row on the UI
        dispatch(
            updateResourceTableRow({
                tableId,
                datumId,
                updateArgs,
                cellStateMappingFunc: optionalCellStateMappingFunc,
            }),
        )

        try {
            // optionalActionCreator overwrites the default network request made with resourcePatchDetail
            if (optionalActionCreator) {
                const response = await dispatch(
                    optionalActionCreator({
                        tableId,
                        datumId,
                        updateArgs,
                        resource,
                    }),
                )
                // update the UI once again with the result from the server. May be different
                // than the previous optimistic render
                dispatch(
                    updateResourceTableRow({
                        tableId,
                        datumId,
                        responseTransform: optionalResponseTransform, // provide a way to clean the response for the action
                        updateArgs: response, // provide a way to clean the response for the action
                        cellStateMappingFunc: optionalCellStateMappingFunc,
                    }),
                )
                // patch the resource with the provided updateArgs
            } else if (resource) {
                const response = await dispatch(
                    djangioActionCreators.resourcePatchDetail(
                        resource,
                        actionGroups.patchResourceTableResource,
                        datumId,
                        updateArgs,
                        {
                            id: tableId,
                            datumId,
                            updateArgs,
                        },
                    ),
                )
                // only update the fields from the response that were patched
                // this helps with rendering / also makes it so that any dehydrate_extra_
                // fields don't get overwritten
                const updateFields = Object.keys(updateArgs)
                const updateArgsFromResponse = objectFrom(
                    Object.entries(response).filter(([key, _]) => updateFields.includes(key)),
                )
                // update the UI once again with the result from the server. May be different
                // than the previous optimistic render
                dispatch(
                    updateResourceTableRow({
                        tableId,
                        datumId,
                        responseTransform: optionalResponseTransform, // provide a way to clean the response for the action
                        updateArgs: updateArgsFromResponse, // provide a way to clean the response for the action
                        cellStateMappingFunc: optionalCellStateMappingFunc,
                    }),
                )
            }
        } catch (e) {
            BACKENDERROR(e, {}, true)
            const columnKey = Object.keys(updateArgs)[0]
            dispatch({
                type: actionTypes.UPDATE_RESOURCE_TABLE_CELL_STATE,
                id: tableId,
                rowId: datumId,
                state: "error",
                columnKey,
            })
        }
    }

// see docstring below, convenience function that takes 1 object as an argument
export const editResourceTableRowPublic = ({
    tableId,
    datumId,
    updateArgs,
    optionalCellStateMappingFunc,
    optionalActionCreator,
}) => editResourceTableRow(tableId, datumId, updateArgs, optionalCellStateMappingFunc, optionalActionCreator)

/**
 * Sort the resources based on a field that follows the precedence model; that is,
 * assuming that there is a field in the model in which ordering is based on, with the
 * higher value being sent before lower values. This takes in what position the specfic resource
 * should be in relation to other resources, and tries to apply the minimum amount of changes to
 * other resources to make that new ordering. For example, if a resource (ex: CustomTagA) wants to be the 3rd item
 * in the list, check the precedence field on [2], set the CustomTagA.precedence to [2].precedence +1,
 * effectively pushing everything after that down one index, and traverse up the list, updating precedences
 * Optimistically render the new sorted list by resorting row_data (actionTypes.RESOURCE_TABLE_REORDER),
 * then send a bulk patch request to update the needed resources.
 * @param {String} tableId: what sheet key is this related to
 * @param {Integer} datumId: what row is being moved
 * @param {String} field: what field is used for ordering in the backend
 * @param {Object} updateArgs: should have newPlace (int - where to relocate resource), and currentPlace (int - where resource is)
 */
export const editResourceTableOrdering = (tableId, datumId, field, updateArgs) => async (dispatch, getState) => {
    if (updateArgs.newPlace === updateArgs.currentPlace) {
        return
    }
    const toUpdate = []
    const resource = getState().sheet.getIn([tableId, "resourceTableConfiguration", "rowResource"])
    const rowData = getState().sheet.getIn([tableId, "row_data"]).toJS()

    const dataWithoutNullOrder = rowData.filter((type) => type[field] === null)

    // making sure that no item has order: null
    // because if the first item has order: null
    // and we request for the second to become the first
    // it's going to set the second to order: 1 as there is no number to compare to
    if (dataWithoutNullOrder.length) {
        const highestOrderRow = rowData.find((row) => row[field])
        let highestOrderNumber = highestOrderRow ? highestOrderRow[field] : 0
        const highestOrderIndex = highestOrderRow ? rowData.findIndex((row) => row[field]) : rowData.length

        if (highestOrderIndex > 0) {
            for (let i = highestOrderIndex - 1; i >= 0; i--) {
                rowData[i][field] = ++highestOrderNumber
                toUpdate.push(rowData[i])
            }
        }
    }

    // find the precedence of the resource that is currently where we want the item to go
    let newPlace = updateArgs.newPlace

    // make new precedence one higher, effectively placing item right where it needs to go and pushing
    // everything else down
    // however, if the item is going down, we need to look to the one below the newPlace
    if (updateArgs.newPlace > updateArgs.currentPlace) {
        newPlace += 1
    }

    let newPrecedence
    if (updateArgs.newPlace === rowData.length - 1) {
        newPlace -= 1
        newPrecedence = rowData[newPlace][field] - 1
    } else {
        newPrecedence = rowData[newPlace][field] + 1
    }

    // item to be moved always needs to be updated, push it to toUpdate with relevant precedence
    const currentItem = rowData[updateArgs.currentPlace]
    currentItem[field] = newPrecedence

    // check if the item is already in the array
    // is it it update it, if not, add it
    const itemAlreadyInArray = toUpdate.findIndex((type) => type["resource_uri"] === currentItem["resource_uri"])
    if (itemAlreadyInArray !== -1) {
        toUpdate[itemAlreadyInArray] = currentItem
    } else {
        toUpdate.push(currentItem)
    }

    // if newPlace is at the top, no more updates needed (item's precedence is now higher than any others)
    if (newPlace >= 0) {
        // grab the item immediately before the new position of the current item
        newPlace--
        let previous = rowData[newPlace]
        // mark what the current item's precedence is
        let nextPrecedence = newPrecedence

        // start iterating up the list to update precedences so order remains intact, marking what
        // needs to be updated in the backend
        while (newPlace >= 0 && previous[field] <= nextPrecedence) {
            previous = rowData[newPlace]
            nextPrecedence++
            previous[field] = nextPrecedence

            const itemAlreadyInArray = toUpdate.findIndex((type) => type["resource_uri"] === previous["resource_uri"])
            if (itemAlreadyInArray !== -1) {
                toUpdate[itemAlreadyInArray] = previous
            } else {
                toUpdate.push(previous)
            }

            newPlace--
            // make sure to skip the rowData of the thing we are moving
            if (newPlace === updateArgs.currentPlace) {
                newPlace--
            }
        }
    }

    // send action to reorder optimistically (just resorting row_data in the store)
    dispatch({
        type: actionTypes.RESOURCE_TABLE_REORDER,
        sheetKey: tableId,
        datumId,
        newPlace: updateArgs.newPlace,
        currentPlace: updateArgs.currentPlace,
    })

    dispatch({
        type: actionTypes.RESOURCE_TABLE_UPDATE,
        sheetKey: tableId,
        toUpdate,
    })

    // make sure to only patch relevant fields (no need to pass every field)
    const toUpdateObjs = toUpdate.map((each) => ({
        resource_uri: each.resource_uri,
        [field]: each[field],
    }))
    const reduxArgs = { id: tableId }
    // send list patch to update all needed resources (including the one moved)
    dispatch(
        djangioActionCreators.resourcePatchList(
            resource,
            actionGroups.patchResourceTableResource,
            toUpdateObjs,
            reduxArgs,
        ),
    )
}

// selects open / closed, used to stop table from scrolling when a select is open to prevent
// a jank UI
export const toggleSelectOpen = (id, selectIsOpen) => ({ type: actionTypes.TOGGLE_SELECT_OPEN, id, selectIsOpen })

/**
 * Remove a row from a resource table
 * Optimistically removes the row from the table
 * Automatically make a PATCH request to the table's resource, archiving the object represented by the datumId
 * If the table's resource does not support archiving (in other words, has an archived field), no network request will be made
 * An optional action creator can be provided to handle custom delete logic. This action creator
 * takes in 1 object as an argument and is passed all of the args passed from the caller function. If an action creator
 * is provided, a network request will NOT be made. Be sure to call the network request in your action creator
 * if your intention is for such a request to be fired
 * @param {String} tableId - The string table id in the store - used to correctly find the data to delete
 * @param {Integer} datumId - the database id of the object that represents the row we are trying to delete. Used
 * to make the network request to archive delete
 * @param {Function} optionalActionCreator - an action creator with the signature ({args}) => Any
 * @returns {Function} - Redux Thunk
 */
export const removeResourceTableRow = (tableId, datumId, optionalActionCreator) => (dispatch, getState) => {
    // grab the resource from the configuration object
    const resource = getState().sheet.getIn([tableId, "resourceTableConfiguration", "rowResource"])

    // Grab the row and hold in memory, in case the removal fails
    const rowToRemove = getState()
        .sheet.getIn([tableId, "row_data"])
        .find((row) => row.get("id") === datumId)

    // remove row from UI
    dispatch({ type: actionTypes.RESOURCE_TABLE_REMOVE_ROW, sheetKey: tableId, datumId })

    const archived = true
    if (optionalActionCreator) {
        return dispatch(
            optionalActionCreator({
                tableId,
                datumId,
                resource,
            }),
        )
        // if the resource supports archiving, patch the resource
    } else if (resource && resource.isArchivable()) {
        return dispatch(
            djangioActionCreators.resourcePatchDetail(
                resource,
                actionGroups.patchResourceTableResource,
                datumId,
                { archived },
                {
                    id: tableId,
                    datumId,
                    archived,
                },
            ),
        ).catch((err) => {
            console.error(err)
            if (err.response && err.response.status === 400) {
                swal({
                    ...swalConfigs.validationError,
                    text: err.response.data.message,
                })
                dispatch({
                    type: actionTypes.RESOURCE_TABLE_RESTORE_ROW,
                    sheetKey: tableId,
                    row: rowToRemove,
                })
            } else {
                swal(swalConfigs.defaultError)
                throw err
            }
        })
    }
}

// Clear all rows of a ResourceTable so that there is no stale data
export const clearResourceTableRows = (tableId) => (dispatch) => {
    // remove row from UI
    dispatch({ type: actionTypes.RESOURCE_TABLE_CLEAR_ALL_ROWS, sheetKey: tableId })
}

/**
 * This action creator is for removing a row from the sheet
 *
 * @param {Integer} id - the id of the sheet, of the form "sheet:1299"
 * @param {Integer} datumId - the database id of the object represented by the row
 * @returns {Function} A Thunk action creator
 */
export const removeRow = (id, datumId) => (dispatch, getState) => {
    // this is probably a bit of an anti pattern, because we're doing some work that should probably be
    // in the reducer, but will prevent a possible race condition
    // as well as patch the SafedSearch with the correct added_ids and deleted_ids
    // typically this work would be handled by Searchify but we are modifying searchify values
    // outside of searchify
    const sheet = getState().sheet.get(String(id)).get("sheet")

    // the datumId is the one we want to delete
    const deletedDatumId = datumId

    // construct a new deleted_ids list, with the new deletedDatumId
    const updatedDeletedIds = sheet.getIn(["rows", "deleted_ids"]).push(deletedDatumId)

    // construct a new added_ids list, with the new deletedDatumId removed
    const updatedAddedIds = sheet.getIn(["rows", "added_ids"]).filter((added_id) => added_id !== deletedDatumId)

    // remove the row from the store
    dispatch({ type: actionTypes.SHEET_REMOVE_ROW, id, datumId })
    // update the added_ids and deleted_ids lists
    dispatch({ type: actionTypes.SHEET_UPDATE_ADDED_IDS, id, updatedAddedIds })
    dispatch({ type: actionTypes.SHEET_UPDATE_DELETED_IDS, id, updatedDeletedIds })

    // send a patch request with the new added_ids and deleted_ids
    dispatch(
        patchSavedSearch(sheet.getIn(["rows", "id"]), {
            deleted_ids: updatedDeletedIds.toJS(),
            added_ids: updatedAddedIds.toJS(),
        }),
    )
}

export const completeSearchify = (id) => ({ type: actionTypes.SHEET_COMPLETE_SEARCHIFY, id })

export const removeSheetFormRowFilter = () => ({ type: actionTypes.REMOVE_SHEET_FORM_ROW_FILTER })

/**
 * Remove a column from the store and from the backend
 * @param {String} id the sheet key of the form "sheet:1299"
 * @param {Integer} columnId the database id of the column
 * @param {Integer} columnIndex the index of the column in the sheet
 * @returns {Function} a function that dispatches another action
 */
export const removeColumn = (id, columnId, columnIndex) => (dispatch) => {
    dispatch({
        type: actionTypes.SHEET_REMOVE_COLUMN,
        id,
        columnIndex,
        columnId,
    })
    return dispatch(patchColumn(id, columnId, { archived: true }))
}

export const clickCell = (id, rowId, columnId, e) => {
    $(".clickedRow").removeClass("clickedRow")
    $(e.target).closest(".fixedDataTableRowLayout_rowWrapper").addClass("clickedRow")
    return {
        type: actionTypes.CLICK_CELL,
        id,
        rowId,
        columnId,
    }
}

/**
 * This action creator gets called when sorting the sheet by a particular column
 * @param {String} id - the sheet key
 * @param {columnIndex} columnIndex - the index of the column relative to sheet
 * @param {sortType} sortType - Whether the sort is ascending, descending, or none
 * @returns {Object} - A Redux Action
 */
export const sortByColumnAtIndex = (id, columnIndex, sortType = sortTypes.DESC) => ({
    type: actionTypes.SORT_BY_COLUMN,
    columnIndex,
    id,
    sortType,
})

/**
 * Action creator to search by a column
 * @param {String} id: the sheet key, of the form "sheet:sheetSlug" or resourceTableId
 * for resource tables
 * @param {Integer} [columnIndex] the index of the column we are searching
 * @param {Integer|String|Object} [value] the value we want to search for
 *
 * @returns {Object} - A Redux action
 */
export const searchByColumnAtIndex = (id, columnIndex, value) => ({
    type: actionTypes.SEARCH_BY_COLUMN,
    value,
    columnIndex,
    id,
})

/**
 * remove a filter from the map to enable sorting by clicking on the map
 * @param {String} id: the sheet key, of the form "sheet:sheetSlug"
 * @param {Integer} the value to filter the sheet on - should correspond to a region enum
 */
export const removeMapFilter = (id, value) => ({ type: actionTypes.REMOVE_MAP_FILTER, value, id })

/**
 * add a filter from the map to enable sorting by clicking on the map
 * @param {String} id: the sheet key, of the form "sheet:sheetSlug"
 * @param {Integer} the value to filter the sheet on - should correspond to a region enum
 */
export const addMapFilter = (id, value) => ({ type: actionTypes.ADD_MAP_FILTER, value, id })

/**
 * for public sheets that have a map, handle toggling whether to show the map
 * @param {String} id: the sheet key, of the form "sheet:sheetSlug"
 */
export const toggleShowMap = (id) => ({ type: actionTypes.TOGGLE_SHOW_MAP, id })

export const clearFilterAndSort = (id) => ({ type: actionTypes.SHEET_CLEAR_FILTER_AND_SORT, id })

export const clearFilter = (id, columnIndex) => ({ type: actionTypes.SHEET_CLEAR_FILTER, id, columnIndex })

export const clearSort = (id) => ({ type: actionTypes.SHEET_CLEAR_SORT, id })

// action creator to toggle whether the download button should show downloading
export const toggleIsDownloading = (sheetKey, isDownloading) => ({
    type: actionTypes.TOGGLE_DOWNLOADING_CSV,
    isDownloading,
    sheetKey,
})

// action creator to toggle whether the create/save sheet button should show loading
export const toggleIsSubmitting = (isSubmitting) => ({ type: actionTypes.TOGGLE_SHEET_SUBMITTING, isSubmitting })

/**
 * Get the primary key out of the sheet key, handling public sheets a little differently
 * @param {String} sheetKey - of the form 'sheet:4912' or 'sheet:llskdjfi3idkdk3e'
 * @param {Object} downloadArguments - for the boolean flag to determine if this is a public sheet
 * @returns {String} - primary key / public_slug for sheet download
 */
const handleSheetPrimaryKey = (sheetKey, { is_public_sheet_download }) =>
    is_public_sheet_download ? sheetKey.replace("sheet:", "") : extractIdFromSheetKey(sheetKey)

/**
 * Dispatch an action that downloads a sheet
 * @param {String} sheetKey - The Sheet key of the form "sheet:2565"
 * @param {Object} downloadArguments - An object consisting of
 * possible filters or sorting conditions, allowing the user to generate
 * a CSV with the same data that they can see in the web app.
 * Can also pass a file format for the download - will default to csv
 * Can also pass in whether this is a public sheet
 * @param {number} quorumDataType - The QDT associated with this sheet for recoding UAs
 * @returns {Promise} - promise with the resolution result
 */
export const downloadSheet =
    (sheetKey, downloadArguments = {}, quorumDataType) =>
    async (dispatch) => {
        const response = await dispatch(
            djangioActionCreators.downloadFile({
                resource: Sheet.objects.filter({ format: downloadArguments.format || "csv" }),
                actionTypes: actionGroups.downloadSheetCSV,
                primaryKey: handleSheetPrimaryKey(sheetKey, downloadArguments),
                requestArgs: downloadArguments,
                payload: { sheetKey },
            }),
        )

        // If this is a public sheet we can't fire a UA
        if (response && !downloadArguments.is_public_sheet_download) {
            sendUserActionPost({
                userActionType: DjangIO.app.useractions.types.UserActionType.sheet_downloaded,
                quorumDataType,
            })
        }

        return response
    }

export const focusColumn = (id, columnIndex) => ({ type: actionTypes.FOCUS_COLUMN, id, columnIndex })

export const unfocusColumn = (id, columnIndex) => ({ type: actionTypes.UNFOCUS_COLUMN, id, columnIndex })

export const mergeVisualizationDict = (id, dict) => ({ type: actionTypes.MERGE_VIS_DICT, id, dict })

export const sheetFormStartSearchify = () => ({ type: actionTypes.START_SHEET_SEARCHIFY, id: "sheetForm" })

export const sheetFormSubmitSearchify =
    ({ id, rowId, searchifyFilters }) =>
    (dispatch) => {
        const rows = {
            id: rowId,
            added_ids: searchifyFilters.addedIds,
            deleted_ids: searchifyFilters.deletedIds,
            filters: searchifyFilters.querysetList,
        }

        dispatch(editSheet(id, { searchifyCount: searchifyFilters.searchifyCount, rows }))
    }

export const favoriteSheetTableRow = (tableId, datumId, updateArgs) => (dispatch, getState) => {
    let newPlace = 0
    let currentPlace
    let lastFavorite = 0

    getState()
        .sheet.getIn([tableId, "row_data"])
        .toJS()
        .map((each, index) => {
            if (each.id === datumId) {
                currentPlace = index
            }
            if (each.is_favorite || (each._extra && each._extra.is_personally_pinned)) {
                lastFavorite = index
            }
        })

    if (!updateArgs.is_favorite) {
        newPlace = lastFavorite
    }

    const resource = getState().sheet.getIn([tableId, "resourceTableConfiguration", "rowResource"])

    // send action to reorder optimistically (just resorting row_data in the store)
    dispatch({
        type: actionTypes.RESOURCE_TABLE_REORDER,
        sheetKey: tableId,
        datumId,
        newPlace,
        currentPlace,
    })

    dispatch({
        type: actionTypes.RESOURCE_TABLE_UPDATE_ROW,
        sheetKey: tableId,
        datumId,
        updateArgs,
    })

    dispatch(
        djangioActionCreators.resourcePatchDetail(
            resource,
            actionGroups.patchResourceTableResource,
            datumId,
            updateArgs,
            {
                id: tableId,
                datumId,
                ...updateArgs,
            },
        ),
    )
}

export const setWindowSize = () => ({
    type: actionTypes.SET_WINDOW_SIZE,
    width: window.innerWidth,
    height: window.innerHeight,
})

/**
 * Takes in whether or not to show the stats button/bar and
 * state is updated and components are optimistically rendering
 *  a patch request is then made and if it's not successful
 *  state is rolled back
 *
 * @param {Boolean} showStats
 * @param {String} sheetKey
 */
export const toggleShowMath = (sheetKey, showStats) => async (dispatch) => {
    dispatch({
        type: actionTypes.SET_SHOULD_SHOW_STATS_BAR,
        showStats,
        sheetKey,
    })
    try {
        // TODO: currently the response contains the columns/row data, use resourcePatchDetail(?) to eleminate this unnessessary info
        await dispatch(patchSheetMathFooter(sheetKey, { should_display_math_bar: showStats }))
    } catch (error) {
        console.error(error)

        // if unsuccessful, rollback state back to previous state
        dispatch({
            type: actionTypes.SET_SHOULD_SHOW_STATS_BAR,
            showStats: !showStats,
            sheetKey,
        })
    }
}

/**
 * Creates a SheetUserInfo object to associate the given Sheet with a set of UserInfos
 *
 * @param {number} id - ID of the given Sheet
   @param {boolean} isPersonallyPinned - new value to update with
 */
export const personallyPinSheetForUser = (id, isPersonallyPinned) => async (dispatch, getState) => {
    let newPlace = 0
    let currentPlace
    let lastFavoriteOrPP = 0 // PP => personally pinned

    getState()
        .sheet.getIn(["sheetListTable", "row_data"])
        .toJS()
        .map((each, index) => {
            // establish where we are
            if (each.id === id) {
                currentPlace = index
            }
            // For the case of pinning, keep track of where it should go to after getting pinned (after last is_favorite)
            if (each.is_favorite) {
                newPlace = index + 1
            }
            // For the case of unpinning, keep track of where it should go to after getting unpinned
            if (each.is_favorite || (each._extra && each._extra.is_personally_pinned)) {
                lastFavoriteOrPP = index
            }
        })

    if (!isPersonallyPinned) {
        newPlace = lastFavoriteOrPP
    }
    // send action to reorder optimistically (just resorting row_data in the store)
    dispatch({
        type: actionTypes.RESOURCE_TABLE_REORDER,
        sheetKey: "sheetListTable",
        datumId: id,
        newPlace: newPlace,
        currentPlace: currentPlace,
    })
    // update UI
    dispatch({
        type: actionTypes.RESOURCE_TABLE_UPDATE_ROW,
        sheetKey: "sheetListTable",
        datumId: id,
        updateArgs: { _extra: { is_personally_pinned: isPersonallyPinned } },
    })

    await dispatch(
        djangioActionCreators.sendAction(Sheet.objects.get(id), actionGroups.personallyPinSheetForUser, {
            action: "update_pinned_user",
            kwargs: { isPersonallyPinned },
            payload: { isPersonallyPinned },
            method: "patch",
        }),
    ).catch((e) => {
        // an error has occurred in the patch, so we need to revert the sheet pinning
        // revert the ordering
        swal("Oops...Something went wrong, please try again.")
        dispatch({
            type: actionTypes.RESOURCE_TABLE_REORDER,
            sheetKey: "sheetListTable",
            datumId: id,
            newPlace: currentPlace,
            currentPlace: newPlace,
        })
        // revert the button -> dropdown
        dispatch({
            type: actionTypes.RESOURCE_TABLE_UPDATE_ROW,
            sheetKey: "sheetListTable",
            datumId: id,
            updateArgs: { _extra: { is_personally_pinned: !isPersonallyPinned } },
        })
    })
}

export const archiveSupporterAction = ({ tableId, datumId }) =>
    djangioActionCreators.resourcePatchDetail(
        DjangIO.app.grassroots.models.NewSupporter,
        actionGroups.patchResourceTableResource,
        datumId,
        { archived: true },
        {
            id: tableId,
            datumId,
            archived: true,
        },
    )
